import { useEffect, useRef, useState } from "react";

import { env } from "@/lib/env";

interface TelegramAuthData {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

declare global {
  interface Window {
    // Set by the widget's `data-onauth` attribute. We register our
    // callback here on mount and tear it down on unmount.
    onTelegramAuth?: (user: TelegramAuthData) => void;
  }
}

/**
 * Telegram Login Widget wrapper. Renders the official inline script that
 * Telegram serves from telegram.org, captures the auth callback, posts
 * it to our `telegram-login` Edge Function, and navigates the user to
 * the magic-link URL the function returns (which lands them on
 * /auth/callback, signed in).
 *
 * Prereqs (set up in @BotFather, not here):
 *   1. `/setdomain` on the bot, value = `meetup.finally.plus`
 *   2. (optional) `/setjoingroups` and `/setprivacy` per your needs
 * Without `/setdomain` the widget refuses to render or fails with a
 * generic "Bot domain invalid" error.
 */
export function TelegramLoginButton({
  size = "large",
  showAvatar = true,
  cornerRadius = 12,
}: {
  size?: "small" | "medium" | "large";
  showAvatar?: boolean;
  cornerRadius?: number;
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Wire the callback Telegram will call into.
    window.onTelegramAuth = async (user) => {
      setError(null);
      setPending(true);
      try {
        const endpoint = `${env.VITE_SUPABASE_URL}/functions/v1/telegram-login`;
        const resp = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(user),
        });
        const data = (await resp.json().catch(() => ({}))) as {
          ok?: boolean;
          token_hash?: string;
          error?: string;
        };
        if (!resp.ok || !data.ok || !data.token_hash) {
          throw new Error(data.error || `Sign-in failed (${resp.status})`);
        }
        // Exchange the hashed token for a real session — same code path
        // as a standard email magic-link click, but skips the redirect
        // through Supabase Auth that would have bounced us to Site URL.
        // Hand off the magic-link exchange to /auth/callback via URL
        // params. Supabase JS auto-detects `token_hash` + `type` in the
        // URL, runs verifyOtp itself, and persists the session — same
        // code path as a regular email magic-link click. AuthCallbackPage
        // then reads users.active_role and routes to the right landing
        // page. This sidesteps the gotcha where verifying right here
        // (with an already-signed-in user from a previous session) could
        // silently fail or leave the UI on /sign-in.
        const url = new URL("/auth/callback", window.location.origin);
        url.searchParams.set("token_hash", data.token_hash);
        url.searchParams.set("type", "magiclink");
        window.location.replace(url.toString());
      } catch (err) {
        setPending(false);
        setError(err instanceof Error ? err.message : "Couldn't sign you in");
      }
    };

    // Inject the official widget script into our wrapper div. We do this
    // imperatively rather than via dangerouslySetInnerHTML so React
    // doesn't see it as part of the render tree (the script tag is
    // single-use; React rehydration would break it).
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.setAttribute("data-telegram-login", env.VITE_TELEGRAM_BOT_USERNAME);
    script.setAttribute("data-size", size);
    script.setAttribute("data-userpic", String(showAvatar));
    script.setAttribute("data-radius", String(cornerRadius));
    script.setAttribute("data-onauth", "onTelegramAuth(user)");
    script.setAttribute("data-request-access", "write");
    wrapperRef.current?.appendChild(script);

    return () => {
      delete window.onTelegramAuth;
      // The script tag stays in the DOM (Telegram doesn't expose a clean
      // teardown), but no callback is registered any more — so a stale
      // widget click after unmount is silently ignored.
    };
  }, [size, showAvatar, cornerRadius]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div ref={wrapperRef} />
      {pending ? (
        <p className="text-[12px] text-[#94a3b8]">Signing you in via Telegram…</p>
      ) : null}
      {error ? (
        <p className="text-[12px] font-semibold text-[#991b1b]">{error}</p>
      ) : null}
    </div>
  );
}
